// src/services/session-prep-service.js
// Client-side service for the AI Session Prep Assistant.
// Calls startSessionPrep and sendPrepMessage Cloud Functions,
// and reads prep session history directly from Firestore.

import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase-config';
import {
  collection, query, where, orderBy, getDocs, getDoc, doc,
} from 'firebase/firestore';

const getCallable = (name) => {
  const functions = getFunctions(undefined, 'us-west1');
  return httpsCallable(functions, name, { timeout: 120000 });
};

/**
 * Start a new AI Session Prep conversation for a client.
 * Creates a new prepSessions document on the backend.
 * @param {string} clientId
 * @returns {Promise<{ prepSessionId: string, dataRichness: object, clientName: string }>}
 */
export async function startPrep(clientId) {
  const fn = getCallable('startSessionPrep');
  const result = await fn({ clientId });
  return result.data;
}

/**
 * Send a message in an existing prep session and receive the AI response.
 * @param {string} prepSessionId
 * @param {string} message
 * @returns {Promise<{ response: string, exchangeCount: number, isLastExchange: boolean }>}
 */
export async function sendMessage(prepSessionId, message) {
  const fn = getCallable('sendPrepMessage');
  const result = await fn({ prepSessionId, message });
  return result.data;
}

/**
 * List all previous prep sessions for a client (most recent first).
 * Used by the "Prep Notes" tab in ClientProfile.
 * @param {string} practitionerId
 * @param {string} clientId
 * @returns {Promise<Array>}
 */
export async function getPrepHistory(practitionerId, clientId) {
  try {
    const q = query(
      collection(db, 'practitioners', practitionerId, 'prepSessions'),
      where('clientId', '==', clientId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('session-prep-service: getPrepHistory failed:', err.message);
    return [];
  }
}

/**
 * Load a single prep session with full message history.
 * Used for read-only view of past prep sessions.
 * @param {string} practitionerId
 * @param {string} prepSessionId
 * @returns {Promise<object|null>}
 */
export async function getPrepSession(practitionerId, prepSessionId) {
  try {
    const ref = doc(db, 'practitioners', practitionerId, 'prepSessions', prepSessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    console.error('session-prep-service: getPrepSession failed:', err.message);
    return null;
  }
}
